<script setup lang="ts" generic="E, T extends DropdownItems">
// @ts-nocheck
import { computed, onMounted, onUnmounted, ref, shallowRef } from 'vue';

export type DropdownOptions = string | number | string[] | number[];

export interface DropdownItems {
  name: string;
  value: DropdownOptions | DropdownOptions[];
}

interface HTMLInputEvent extends Event {
  target: EventTarget & HTMLElement;
}

defineOptions({ inheritAttrs: false });

const modelValue = defineModel<DropdownOptions>('value', { required: false });

const props = withDefaults(
  defineProps<{
    label?: string;
    options?: T[];
    placeholder?: string;
    dropdownClass?: string;
    inputClass?: string;
  }>(),
  {
    placeholder: '',
    inputClass: '',
    dropdownClass: '',
  }
);

const details = ref<HTMLDetailsElement | null>(null);
const search = ref<string | null>('');
const selected = ref<DropdownOptions | DropdownOptions[]>([]);
const focusedIndex = ref<number>(-1);

const options = computed(() =>
  search.value === '' ? props.options : props.options?.filter(({ name }) => name.toLowerCase().includes(search.value?.toLowerCase() ?? ''))
);

const includes = (value: DropdownOptions | DropdownOptions[]): boolean =>
  typeof modelValue.value === 'object' ? modelValue.value.includes(value) : modelValue.value === value;

function selectOption(item?: DropdownItems) {
  if (!item) return;

  if (typeof modelValue.value === 'object') {
    const selectedIndex = modelValue.value.findIndex((opt: DropdownOptions) => opt === item.value);

    if (selectedIndex > -1) {
      modelValue.value.splice(selectedIndex, 1);
      selected.value?.splice(selectedIndex, 1);
    } else {
      modelValue.value.push(item.value);
      selected.value?.push(item.name);
    }

    return;
  }

  if (modelValue.value === item.value) {
    modelValue.value = '';
    selected.value = '';
  } else {
    modelValue.value = item.value;
    selected.value = item.name!;
  }

  details.value?.removeAttribute('open');
  focusedIndex.value = -1;
}

const eventMap = shallowRef<{ [key: string]: () => void }>({
  ArrowDown: () => (focusedIndex.value = (focusedIndex.value + 1) % options.value.length),
  ArrowUp: () => (focusedIndex.value = (focusedIndex.value - 1 + options.value.length) % options.value.length),
  Enter: () => selectOption(options.value[focusedIndex.value === -1 ? 0 : focusedIndex.value]),
  Escape: () => selectOption(options.value[focusedIndex.value === -1 ? 0 : focusedIndex.value]),
  // Tab: () => selectOption(options.value[focusedIndex.value === -1 ? 0 : focusedIndex.value]),
});

function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'Tab') {
    details.value?.removeAttribute('open'); // Close dropdown when tabbing out
    return;
  }

  // Handle other keys
  event.preventDefault(); // prevent default behavior for handled keys
  eventMap.value[event.key] && eventMap.value[event.key]();
}

function handleFocus() {
  details.value?.setAttribute('open', 'true');
}

function handleClickOutside({ target }: any) {
  !details.value?.contains(target) && details.value?.removeAttribute('open');
}

onMounted(() => {
  selected.value = props.options?.filter(({ value }) => includes(value))?.map(({ name }) => name) ?? [];
  document?.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document?.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <fieldset v-bind="$attrs">
    <label v-if="label">{{ label }}</label>
    <details tabindex="0" ref="details" @keydown="handleKeyDown" @focus="handleFocus">
      <summary
        @input="(event: HTMLInputEvent) => (search = event.target?.textContent)"
        contenteditable="false"
        type="input"
        :data-placeholder="placeholder"
        :class="`summary ${inputClass}`"
      >
        <template v-if="$slots.placeholder && !modelValue">
          <slot name="placeholder" class="placeholder"></slot>
        </template>
        <template v-else>
          {{ selected?.toString().replace(/,/g, ', ') }}
        </template>
      </summary>
      <template v-if="$slots.default">
        <ul :class="`ul ${dropdownClass}`">
          <slot></slot>
        </ul>
      </template>
      <template v-else>
        <ul :class="`ul ${dropdownClass}`">
          <template v-for="(item, index) in options" :key="index">
            <li
              class="li"
              :class="[includes(item.value) ? 'selected' : '', focusedIndex === index ? 'focused' : '']"
              @click="selectOption(item)"
            >
              {{ item.name }}
            </li>
          </template>
        </ul>
      </template>
    </details>
  </fieldset>
</template>

<style scoped lang="scss">
[contentEditable='false']:empty:before {
  content: attr(data-placeholder);
  color: gray;
}

[contentEditable='falses']:focus-visible {
  outline: 2px solid transparent;
  outline-offset: 2px;
}

details > summary {
  list-style: none;
}

details > summary::marker,
details > summary::-webkit-details-marker {
  display: none;
}

fieldset {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-inline: 0;
  padding-inline: 0;
  padding-block: 0;

  details {
    position: relative;
  }

  .summary {
    text-overflow: ellipsis;
    white-space: nowrap;
    border-radius: 4px;
    padding: 16px 24px;
    display: flex;
    list-style: none;
    display: flex;
    align-items: center;
    position: relative;
    border: 0;
    border: 1px solid var(--gray-alt);
    background: transparent;
    outline: none;
    border-radius: 4px;
    appearance: none;
    -moz-appearance: none;
    -webkit-appearance: none;
    box-shadow: none;
    cursor: pointer;
    outline: none;
    transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
    box-sizing: border-box;
    height: 56px;
  }

  .ul {
    margin: 0px !important;
    position: absolute;
    display: block;
    width: -webkit-fill-available;
    background-color: white;
    z-index: 40;
    box-shadow:
      0 1px 3px rgba(0, 0, 0, 0.1),
      0 1px 2px rgba(0, 0, 0, 0.06);
    border: 1px solid #e2e8f0;
    border-radius: 0.25rem;
    padding-inline-start: 0px;
    margin-block: 0;
    max-height: 500px;
    overflow: auto;

    &::-webkit-scrollbar {
      display: none;
    }

    &::-webkit-scrollbar {
      width: 5px;
      height: 8px;
      background-color: #aaa;
    }

    &::-webkit-scrollbar-thumb {
      background: #000;
    }
  }

  .li {
    display: flex;
    justify-content: space-between;
    cursor: pointer;
    margin: 0.25rem;
    font-size: 0.875rem;
    padding: 0.5rem;
    border-radius: 0.25rem;

    &:hover {
      background-color: #f7fafc;
    }
  }

  .selected {
    border-radius: 0.25rem;
    box-shadow: inset 0 0 0 1px var(--main);
  }

  .focused {
    background-color: #edf2f7;
  }
}
</style>
